import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dateLabel, messagingRequest as api, requestKey } from './api';
import { Field, Status } from './UI';

export default function Inbox({ meta, initialContact, notify }) {
  const [threads, setThreads] = useState([]),
    [unmatched, setUnmatched] = useState([]),
    [query, setQuery] = useState(''),
    [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState(initialContact || ''),
    [thread, setThread] = useState(null),
    [loading, setLoading] = useState(false),
    [reply, setReply] = useState(''),
    [busy, setBusy] = useState(false);
  const [showPermission, setShowPermission] = useState(false),
    [channel, setChannel] = useState('sms'),
    [evidence, setEvidence] = useState(''),
    [permissionDate, setPermissionDate] = useState(new Date().toISOString().slice(0, 10)),
    [confirm, setConfirm] = useState(false);
  const replyKey = useRef(requestKey()),
    sending = useRef(false),
    selection = useRef(selected),
    messagePane = useRef(null),
    atBottom = useRef(true);
  selection.current = selected;
  const loadInbox = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api('/inbox'), api('/unmatched')]);
      setThreads(a.threads);
      setUnmatched(b.messages);
    } catch (e) {
      notify(e.message, 'error');
    }
  }, [notify]);
  const loadThread = useCallback(async value => {
    const result = await api('/contacts/' + value + '/thread');
    if (selection.current === value) setThread(result);
  }, []);
  useEffect(() => {
    loadInbox();
    const timer = setInterval(loadInbox, 30000);
    return () => clearInterval(timer);
  }, [loadInbox]);
  useEffect(() => {
    if (initialContact) setSelected(initialContact);
  }, [initialContact]);
  useEffect(() => {
    atBottom.current = true;
    setThread(null);
    setReply('');
    replyKey.current = requestKey();
    setShowPermission(false);
    setConfirm(false);
    if (!selected) return;
    setLoading(true);
    let current = true;
    loadThread(selected)
      .catch(e => {
        if (current) notify(e.message, 'error');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    const timer = setInterval(() => loadThread(selected).catch(() => {}), 30000);
    return () => {
      current = false;
      clearInterval(timer);
    };
  }, [selected, loadThread, notify]);
  useEffect(() => {
    const pane = messagePane.current;
    if (pane && atBottom.current) pane.scrollTop = pane.scrollHeight;
  }, [thread]);
  const work = async fn => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  const search = e => {
    e.preventDefault();
    work(async () => {
      const result = await api('/contacts?q=' + encodeURIComponent(query));
      setMatches(result.contacts);
    });
  };
  const open = id => {
    if (!reply || window.confirm('Discard your unsent reply?')) setSelected(id);
  };
  const send = e => {
    e.preventDefault();
    work(async () => {
      const result = await api('/contacts/' + selected + '/reply', { body: reply, requestKey: replyKey.current });
      setReply('');
      replyKey.current = requestKey();
      notify('Reply queued for ' + dateLabel(result.dueAt) + '.');
      await loadThread(selected);
      loadInbox();
    });
  };
  const recordPermission = granted =>
    work(async () => {
      await api(
        '/contacts/' + selected + '/permission',
        { channel, granted, evidence, occurredAt: permissionDate, confirm },
        'PUT'
      );
      setConfirm(false);
      setEvidence('');
      notify(
        granted
          ? 'Permission recorded for this contact address.'
          : 'Marketing messages to this address have been stopped.'
      );
      await loadThread(selected);
    });
  return (
    <section className={'msg-inbox ' + (selected ? 'msg-inbox-selected' : '')}>
      <aside className="msg-inbox-list" aria-label="Text conversations">
        <div className="msg-list-heading">
          <h2>Text inbox</h2>
          <button className="msg-link" onClick={loadInbox}>
            Refresh
          </button>
        </div>
        <form className="msg-contact-search" onSubmit={search}>
          <label className="sr-only" htmlFor="msg-contact-search">
            Find a CRM customer
          </label>
          <input
            id="msg-contact-search"
            placeholder="Find a CRM customer…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <button className="msg-button secondary" disabled={busy}>
            Find
          </button>
        </form>
        {matches !== null && (
          <div className="msg-search-results">
            <div className="msg-list-heading">
              <strong>Customers · first 50</strong>
              <button className="msg-link" onClick={() => setMatches(null)}>
                Clear
              </button>
            </div>
            {matches.length ? (
              matches.map(c => (
                <button className="msg-thread-item" key={c._id} onClick={() => open(c._id)}>
                  <strong>{c.buyer}</strong>
                  <span>{c.status}</span>
                </button>
              ))
            ) : (
              <p className="msg-muted">No matching customers.</p>
            )}
          </div>
        )}
        <div className="msg-thread-list">
          {threads.length ? (
            threads.map(t => (
              <button
                className={'msg-thread-item ' + (selected === t.contact?._id ? 'selected' : '')}
                key={t.contact?._id}
                onClick={() => open(t.contact._id)}
              >
                <div>
                  <strong>{t.contact?.buyer || 'Customer'}</strong>
                  {t.unread > 0 && <b className="msg-unread">{t.unread}</b>}
                </div>
                <span>{t.last.body}</span>
                <small>{dateLabel(t.last.at)}</small>
              </button>
            ))
          ) : (
            <p className="msg-muted msg-padded">
              No text conversations yet. Find a customer to record permission or start a conversation.
            </p>
          )}
        </div>
        {unmatched.length > 0 && (
          <details className="msg-unmatched">
            <summary>{unmatched.length} replies need a customer match</summary>
            {unmatched.map(m => (
              <article key={m._id}>
                <strong>{m.destination}</strong>
                <p>{m.body}</p>
                <small>Find the customer above, then match the reply. Their phone number and store must match.</small>
                <button
                  className="msg-button secondary"
                  disabled={!selected || busy}
                  onClick={() =>
                    work(async () => {
                      await api('/unmatched/' + m._id + '/assign', { contactId: selected });
                      notify('Reply matched to the customer.');
                      loadInbox();
                      loadThread(selected);
                    })
                  }
                >
                  Match to selected customer
                </button>
              </article>
            ))}
          </details>
        )}
      </aside>
      <div className="msg-conversation" aria-label="Selected conversation">
        {!selected ? (
          <div className="msg-empty">
            <span className="msg-empty-icon" aria-hidden="true">
              ☏
            </span>
            <h3>Keep the conversation together</h3>
            <p>Select a conversation or find a CRM customer.</p>
          </div>
        ) : (
          <>
            <div className="msg-conversation-header">
              <button className="msg-link msg-back-inbox" onClick={() => open('')}>
                ← Inbox
              </button>
              <div>
                <h2>{thread?.contact.buyer || (loading ? 'Loading customer…' : 'Customer')}</h2>
                <small>
                  {thread?.contact.cellPhone || 'No phone saved'} · {thread?.contact.status}
                </small>
              </div>
              {thread && (
                <Link className="msg-button secondary" to={'/crm/' + selected}>
                  Open record
                </Link>
              )}
            </div>
            {thread && (
              <>
                <div className="msg-permission-bar">
                  <div>
                    {['sms', 'email'].map(ch => {
                      const p = thread.permissions.find(p => p.channel === ch);
                      const state = thread.suppressed.includes(ch)
                        ? 'Opted out'
                        : p?.granted && p.destinationCurrent
                        ? 'Permission recorded'
                        : 'Permission needed';
                      return (
                        <span key={ch}>
                          {ch === 'sms' ? 'Text' : 'Email'}: <strong>{state}</strong>
                        </span>
                      );
                    })}
                  </div>
                  <button className="msg-link" onClick={() => setShowPermission(v => !v)}>
                    {showPermission ? 'Close permissions' : 'Manage permissions'}
                  </button>
                </div>
                {showPermission && (
                  <div className="msg-permission-editor">
                    <div className="msg-form-pair">
                      <Field label="Channel">
                        <select value={channel} onChange={e => setChannel(e.target.value)}>
                          <option value="sms">Text marketing</option>
                          <option value="email">Email marketing</option>
                        </select>
                      </Field>
                      <Field
                        label="Permission received"
                        type="date"
                        value={permissionDate}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={e => setPermissionDate(e.target.value)}
                      />
                    </div>
                    <Field
                      label="Where and when did the customer give permission?"
                      value={evidence}
                      onChange={e => setEvidence(e.target.value)}
                      maxLength={1000}
                      placeholder="Example: signed marketing opt-in on the store form, October 12"
                    />
                    <label className="msg-checkbox">
                      <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} />I have
                      evidence of this customer’s permission for marketing on this channel.
                    </label>
                    <div className="msg-inline-actions">
                      <button
                        className="msg-button"
                        disabled={busy || !confirm || !evidence.trim() || thread.suppressed.includes(channel)}
                        onClick={() => recordPermission(true)}
                      >
                        Record permission
                      </button>
                      <button
                        className="msg-button secondary"
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm('Stop marketing messages to this customer address?'))
                            recordPermission(false);
                        }}
                      >
                        Record opt-out
                      </button>
                      {meta.user.canConfigure && thread.suppressed.includes(channel) && (
                        <button
                          className="msg-button secondary"
                          disabled={busy || !confirm || !evidence.trim()}
                          onClick={() =>
                            work(async () => {
                              await api('/contacts/' + selected + '/resubscribe', {
                                channel,
                                evidence,
                                occurredAt: permissionDate,
                                confirm
                              });
                              await loadThread(selected);
                              notify('Documented re-subscription saved.');
                            })
                          }
                        >
                          Review re-subscription
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div
                  ref={messagePane}
                  onScroll={() => {
                    const p = messagePane.current;
                    atBottom.current = p.scrollHeight - p.scrollTop - p.clientHeight < 80;
                  }}
                  className="msg-conversation-scroll"
                  role="log"
                  aria-label="Customer messages"
                >
                  {thread.messages.length ? (
                    thread.messages.map(m => (
                      <article className={'msg-bubble ' + m.direction} key={m._id}>
                        <div>
                          <span>
                            {m.direction === 'inbound' ? thread.contact.buyer : 'MHC'} ·{' '}
                            {m.channel === 'sms' ? 'Text' : 'Email'}
                          </span>
                          <time>{dateLabel(m.createdAt)}</time>
                        </div>
                        {m.subject && <strong>{m.subject}</strong>}
                        <p>{m.body}</p>
                        <footer>
                          <Status value={m.status} />
                          {m.reason && <small>{m.reason}</small>}
                        </footer>
                      </article>
                    ))
                  ) : (
                    <p className="msg-muted msg-padded">No messages have been sent through this CRM yet.</p>
                  )}
                </div>
                <form className="msg-reply" onSubmit={send}>
                  <label htmlFor="msg-reply">Reply by text</label>
                  <textarea
                    id="msg-reply"
                    rows={3}
                    value={reply}
                    maxLength={1200}
                    onChange={e => {
                      setReply(e.target.value);
                      replyKey.current = requestKey();
                    }}
                    placeholder="Write your reply…"
                    disabled={busy || thread.suppressed.includes('sms')}
                  />
                  <div>
                    <small>Replies use the store’s texting number. STOP instructions are added automatically.</small>
                    <button
                      className="msg-button"
                      disabled={busy || !reply.trim() || thread.suppressed.includes('sms')}
                    >
                      {busy ? 'Queuing…' : 'Send reply'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
